import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Add,
  Phone,
  Email,
  MeetingRoom,
  Visibility,
  Edit,
  Delete,
  Schedule,
} from '@mui/icons-material';
import { ClientInteraction, InteractionType, InteractionOutcome } from '@/types';
import { ClientService } from '@/services/clientService';
import { useAuthStore } from '@/store/authStore';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';

interface ClientInteractionProps {
  clientId: string;
  interactions: ClientInteraction[];
  onInteractionAdded?: () => void;
  onInteractionUpdated?: () => void;
  onInteractionDeleted?: () => void;
}

const ClientInteractionComponent: React.FC<ClientInteractionProps> = ({
  clientId,
  interactions,
  onInteractionAdded,
  onInteractionUpdated,
  onInteractionDeleted,
}) => {
  const { user } = useAuthStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState<ClientInteraction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    type: InteractionType.CALL,
    notes: '',
    outcome: '' as InteractionOutcome | '',
    followUpDate: '',
    propertyId: '',
  });

  const interactionTypeIcons = {
    [InteractionType.CALL]: <Phone />,
    [InteractionType.EMAIL]: <Email />,
    [InteractionType.MEETING]: <MeetingRoom />,
    [InteractionType.SHOWING]: <Visibility />,
    [InteractionType.VIEW]: <Visibility />,
    [InteractionType.INQUIRY]: <Email />,
    [InteractionType.FAVORITE]: <Schedule />,
    [InteractionType.SHARE]: <Schedule />,
    [InteractionType.OFFER]: <Schedule />,
    [InteractionType.CONTRACT]: <Schedule />,
  };

  const interactionTypeLabels = {
    [InteractionType.CALL]: 'Call',
    [InteractionType.EMAIL]: 'Email',
    [InteractionType.MEETING]: 'Meeting',
    [InteractionType.SHOWING]: 'Showing',
    [InteractionType.VIEW]: 'View',
    [InteractionType.INQUIRY]: 'Inquiry',
    [InteractionType.FAVORITE]: 'Favorite',
    [InteractionType.SHARE]: 'Share',
    [InteractionType.OFFER]: 'Offer',
    [InteractionType.CONTRACT]: 'Contract',
  };

  const outcomeColors = {
    [InteractionOutcome.POSITIVE]: 'success',
    [InteractionOutcome.NEUTRAL]: 'default',
    [InteractionOutcome.NEGATIVE]: 'error',
    [InteractionOutcome.PENDING]: 'warning',
    [InteractionOutcome.FOLLOW_UP]: 'info',
  } as const;

  const handleOpenDialog = (interaction?: ClientInteraction) => {
    if (interaction) {
      setEditingInteraction(interaction);
      setFormData({
        type: interaction.type,
        notes: interaction.notes || '',
        outcome: interaction.outcome || '',
        followUpDate: interaction.followUpDate
          ? format(interaction.followUpDate.toDate(), 'yyyy-MM-dd')
          : '',
        propertyId: interaction.propertyId || '',
      });
    } else {
      setEditingInteraction(null);
      setFormData({
        type: InteractionType.CALL as InteractionType,
        notes: '',
        outcome: '',
        followUpDate: '',
        propertyId: '',
      });
    }
    setDialogOpen(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingInteraction(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const interactionData = {
        type: formData.type,
        agentId: user.id,
        notes: formData.notes || undefined,
        outcome: formData.outcome || undefined,
        followUpDate: formData.followUpDate
          ? Timestamp.fromDate(new Date(formData.followUpDate))
          : undefined,
        propertyId: formData.propertyId || undefined,
      };

      if (editingInteraction) {
        await ClientService.updateClientInteraction(clientId, editingInteraction.id, interactionData);
        onInteractionUpdated?.();
      } else {
        await ClientService.addClientInteraction(clientId, interactionData);
        onInteractionAdded?.();
      }

      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save interaction');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    setLoading(true);
    try {
      await ClientService.deleteClientInteraction(clientId, deletingId);
      onInteractionDeleted?.();
      setDeleteDialogOpen(false);
      setDeletingId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete interaction');
    } finally {
      setLoading(false);
    }
  };

  const sortedInteractions = [...interactions].sort((a, b) => {
    const dateA = a.timestamp.toDate().getTime();
    const dateB = b.timestamp.toDate().getTime();
    return dateB - dateA; // Most recent first
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Interactions ({interactions.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          size="small"
        >
          Add Interaction
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {sortedInteractions.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              No interactions yet. Add your first interaction to start tracking client communication.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {sortedInteractions.map((interaction, index) => (
            <React.Fragment key={interaction.id}>
              <ListItem
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  mb: 1,
                  boxShadow: 1,
                  '&:hover': {
                    boxShadow: 2,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {interactionTypeIcons[interaction.type]}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {interactionTypeLabels[interaction.type]}
                      </Typography>
                      {interaction.outcome && (
                        <Chip
                          label={interaction.outcome}
                          size="small"
                          color={outcomeColors[interaction.outcome]}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {format(interaction.timestamp.toDate(), 'PPp')}
                    </Typography>
                    {interaction.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {interaction.notes}
                      </Typography>
                    )}
                    {interaction.followUpDate && (
                      <Chip
                        icon={<Schedule />}
                        label={`Follow-up: ${format(interaction.followUpDate.toDate(), 'PP')}`}
                        size="small"
                        color="warning"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </Box>
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleOpenDialog(interaction)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => {
                        setDeletingId(interaction.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
              {index < sortedInteractions.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingInteraction ? 'Edit Interaction' : 'Add New Interaction'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value as InteractionType })}
              >
                {Object.values(InteractionType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {interactionTypeLabels[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Property ID (optional)"
              value={formData.propertyId}
              onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
              placeholder="Enter property ID if related"
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add notes about this interaction..."
            />

            <FormControl fullWidth>
              <InputLabel>Outcome</InputLabel>
              <Select
                value={formData.outcome}
                label="Outcome"
                onChange={(e) =>
                  setFormData({ ...formData, outcome: e.target.value as InteractionOutcome })
                }
              >
                <MenuItem value="">None</MenuItem>
                {Object.values(InteractionOutcome).map((outcome) => (
                  <MenuItem key={outcome} value={outcome}>
                    {outcome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="date"
              label="Follow-up Date (optional)"
              value={formData.followUpDate}
              onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {editingInteraction ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Interaction?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this interaction? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientInteractionComponent;

